/**
 * Centralized Branding Configuration
 *
 * Edit this single file to change ALL branding across the entire dashboard.
 * Colors, logos, and company name are all controlled from here.
 *
 * After editing, the entire app will reflect the new branding automatically.
 */

export interface BrandingConfig {
  company: {
    // Internal identifier used for MongoDB database/collection names. Do not change after launch.
    name: string;
    // Customer-facing brand name for UI, browser title, and communications.
    displayName?: string;
    tagline?: string;
    copyright?: string;
  };
  logos: {
    primary: string;      // Main logo (e.g., "/logo.png")
    wordmark: string;     // Text/wordmark logo (e.g., "/wordmark.svg")
    favicon?: string;     // Optional favicon path
  };
  colors: {
    primary: string;      // Main brand color
    secondary: string;    // Secondary/gradient start color
    tertiary: string;     // Tertiary/gradient end color
    accent: string;       // Accent/highlight color
    ring: string;         // Focus ring color
    gradient: {
      start: string;
      end: string;
      angle: number;      // Gradient angle in degrees
    };
    chart: string[];      // Chart color palette (5+ colors recommended)
    // Page background colors (light and dark mode)
    background?: {
      light: string;      // Background color for light mode (default: #f5f5f5)
      dark: string;       // Background color for dark mode (default: #0a0a0a)
    };
    // Card/panel background colors
    card?: {
      light: string;      // Card background for light mode (default: #ffffff)
      dark: string;       // Card background for dark mode (default: #171717)
    };
  };
  email: {
    headerGradientStart: string;
    headerGradientEnd: string;
    buttonBackground: string;
    accentText: string;
  };
}

/**
 * ============================================
 * EDIT BELOW TO CHANGE BRANDING
 * ============================================
 */
export const branding: BrandingConfig = {
  company: {
    // Keep this stable: it determines the existing infoscalers MongoDB collections.
    name: "InfoScalers",
    displayName: "Infoscalers.io",
    tagline: "Sales Dashboard",
    copyright: "Infoscalers.io",
  },

  logos: {
    primary: "/infoscalers-logo.png",
    wordmark: "/infoscalers-logo.png",
    favicon: "/infoscalers-logo.png",
  },

  colors: {
    // InfoScaler brand colors - Coral red + dark theme
    primary: "#E85B5B",      // InfoScaler coral red - primary actions
    secondary: "#F06B6B",    // Lighter coral - gradient start
    tertiary: "#D14545",     // Deeper red - gradient end
    accent: "#FDDEDE",       // Soft pink tint - highlights, badges
    ring: "#E85B5B",         // Coral red - focus rings

    // Gradient definition
    gradient: {
      start: "#F06B6B",      // Lighter coral
      end: "#D14545",        // Deeper red
      angle: 135,
    },

    // Chart color palette
    chart: [
      "#E85B5B",  // Coral red (brand)
      "#6366F1",  // Indigo
      "#14B8A6",  // Teal
      "#F59E0B",  // Amber
      "#8B5CF6",  // Violet
      "#10B981",  // Emerald
      "#3B82F6",  // Blue
      "#EC4899",  // Pink
    ],

    // Page background colors
    background: {
      light: "#f5f5f5",
      dark: "#0a0a0a",    // Near-black like Urban Unity
    },

    // Card/panel background colors
    card: {
      light: "#ffffff",
      dark: "#171717",    // Dark card like Urban Unity
    },
  },

  email: {
    headerGradientStart: "#F06B6B",
    headerGradientEnd: "#D14545",
    buttonBackground: "#E85B5B",
    accentText: "#E85B5B",
  },
};

// ============================================
// HELPER FUNCTIONS (do not edit)
// ============================================

/**
 * Generate CSS gradient string from config
 */
export function getGradientCSS(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}

/**
 * Generate Tailwind gradient classes
 * Returns: "from-[#11b2f8] to-[#504df9]"
 */
export function getGradientTailwind(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}

/**
 * Get chart color by index (wraps around if index exceeds array length)
 */
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  if (chart.length === 0) return branding.colors.primary;
  return chart[index % chart.length] ?? branding.colors.primary;
}

/**
 * Inject all branding CSS variables into the document root
 * Called automatically by ThemeProvider (backward-compatible wrapper)
 */
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors } = branding;

  // Core brand colors
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);

  // Gradient colors
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors
  colors.chart.forEach((color, i) => {
    root.style.setProperty(`--chart-${i + 1}`, color);
  });

  // Background colors (with defaults)
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark = colors.background?.dark ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark = colors.card?.dark ?? "#171717";

  root.style.setProperty("--brand-bg-light", bgLight);
  root.style.setProperty("--brand-bg-dark", bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark", cardDark);

  // Also update the semantic color variables
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);

  // Update background based on current theme
  const isDark = root.classList.contains("dark");
  const isCrimson = root.classList.contains("crimson");
  if (isCrimson) {
    root.style.setProperty("--background", "transparent");
    root.style.setProperty("--card", "rgba(255, 255, 255, 0.93)");
    root.style.setProperty("--foreground", "#ffffff");
    root.style.setProperty("--card-foreground", "#1a0404");
    root.style.setProperty("--popover", "rgba(255, 255, 255, 0.97)");
    root.style.setProperty("--popover-foreground", "#1a0404");
    root.style.setProperty("--border", "rgba(255, 255, 255, 0.28)");
    root.style.setProperty("--muted-foreground", "rgba(255,255,255,0.75)");
  } else {
    root.style.setProperty("--background", isDark ? bgDark : bgLight);
    root.style.setProperty("--card", isDark ? cardDark : cardLight);
  }
}

/**
 * Inject full theme CSS variables from a ThemePreset.
 * Sets brand colors, semantic vars, typography, card styling, and component vars.
 */
export function injectThemeCSS(
  preset: import("./theme-presets").ThemePreset,
  isDark: boolean
): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors, semantic, typography, cardStyle, components, scrollbar } = preset;
  const mode = isDark ? "dark" : "light";
  const sem = semantic[mode];

  // ── Brand colors ──
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors
  colors.chart.forEach((color, i) => {
    root.style.setProperty(`--chart-${i + 1}`, color);
  });

  // Background / card brand vars
  root.style.setProperty("--brand-bg-light", colors.background.light);
  root.style.setProperty("--brand-bg-dark", colors.background.dark);
  root.style.setProperty("--brand-card-light", colors.card.light);
  root.style.setProperty("--brand-card-dark", colors.card.dark);

  // ── Semantic color variables ──
  root.style.setProperty("--background", sem.background);
  root.style.setProperty("--foreground", sem.foreground);
  root.style.setProperty("--card", sem.card);
  root.style.setProperty("--card-foreground", sem.cardForeground);
  root.style.setProperty("--popover", sem.popover);
  root.style.setProperty("--popover-foreground", sem.popoverForeground);
  root.style.setProperty("--primary", sem.primary);
  root.style.setProperty("--primary-foreground", sem.primaryForeground);
  root.style.setProperty("--secondary", sem.secondary);
  root.style.setProperty("--secondary-foreground", sem.secondaryForeground);
  root.style.setProperty("--muted", sem.muted);
  root.style.setProperty("--muted-foreground", sem.mutedForeground);
  root.style.setProperty("--accent", sem.accent);
  root.style.setProperty("--accent-foreground", sem.accentForeground);
  root.style.setProperty("--destructive", sem.destructive);
  root.style.setProperty("--border", sem.border);
  root.style.setProperty("--input", sem.input);
  root.style.setProperty("--ring", sem.ring);
  root.style.setProperty("--sidebar", sem.sidebar);
  root.style.setProperty("--sidebar-foreground", sem.sidebarForeground);
  root.style.setProperty("--sidebar-primary", sem.sidebarPrimary);
  root.style.setProperty("--sidebar-primary-foreground", sem.sidebarPrimaryForeground);
  root.style.setProperty("--sidebar-accent", sem.sidebarAccent);
  root.style.setProperty("--sidebar-accent-foreground", sem.sidebarAccentForeground);
  root.style.setProperty("--sidebar-border", sem.sidebarBorder);
  root.style.setProperty("--sidebar-ring", sem.sidebarRing);

  // ── Typography ──
  root.style.setProperty("--theme-font-family", preset.fontFamily);
  root.style.setProperty("--theme-page-title-size", typography.pageTitle.fontSize);
  root.style.setProperty("--theme-page-title-weight", typography.pageTitle.fontWeight);
  root.style.setProperty("--theme-page-title-tracking", typography.pageTitle.letterSpacing ?? "normal");
  root.style.setProperty("--theme-section-title-size", typography.sectionTitle.fontSize);
  root.style.setProperty("--theme-section-title-weight", typography.sectionTitle.fontWeight);
  root.style.setProperty("--theme-card-title-size", typography.cardTitle.fontSize);
  root.style.setProperty("--theme-card-title-weight", typography.cardTitle.fontWeight);
  root.style.setProperty("--theme-kpi-value-size", typography.kpiValue.fontSize);
  root.style.setProperty("--theme-kpi-value-weight", typography.kpiValue.fontWeight);
  root.style.setProperty("--theme-body-size", typography.body.fontSize);
  root.style.setProperty("--theme-body-weight", typography.body.fontWeight);
  root.style.setProperty("--theme-small-size", typography.small.fontSize);

  // ── Card styling ──
  root.style.setProperty("--theme-card-radius", cardStyle.borderRadius);
  root.style.setProperty("--theme-card-padding", cardStyle.padding);
  root.style.setProperty("--theme-card-shadow", isDark ? cardStyle.shadow.dark : cardStyle.shadow.light);
  root.style.setProperty("--theme-card-backdrop", cardStyle.backdropBlur);

  // ── Components ──
  root.style.setProperty("--theme-button-radius", components.buttonRadius);
  root.style.setProperty("--theme-input-radius", components.inputRadius);
  root.style.setProperty("--radius", components.baseRadius);

  // ── Layout ──
  const { layout } = preset;
  root.style.setProperty("--theme-icon-box-size", layout.iconBoxSize);
  root.style.setProperty("--theme-icon-box-radius", layout.iconBoxRadius);
  root.style.setProperty("--theme-icon-size", layout.iconSize);
  root.style.setProperty("--theme-kpi-grid-min", layout.kpiGridMin);
  root.style.setProperty("--theme-grid-gap", layout.gridGap);
  root.style.setProperty("--theme-content-padding", layout.contentPadding);
  root.style.setProperty("--theme-label-size", layout.labelSize);

  // ── Scrollbar ──
  root.style.setProperty("--theme-scrollbar-width", scrollbar.width);
  root.style.setProperty("--theme-scrollbar-thumb", scrollbar.thumbColor);

  // ── Font loading ──
  const existingFontLink = document.getElementById("theme-font") as HTMLLinkElement | null;
  if (preset.fontUrl) {
    if (preset.fontUrl.endsWith(".woff2")) {
      // Direct font file — handled by @font-face in CSS, no link needed
      if (existingFontLink) existingFontLink.remove();
    } else {
      // Google Fonts stylesheet URL
      if (existingFontLink) {
        existingFontLink.href = preset.fontUrl;
      } else {
        const link = document.createElement("link");
        link.id = "theme-font";
        link.rel = "stylesheet";
        link.href = preset.fontUrl;
        document.head.appendChild(link);
      }
    }
  } else if (existingFontLink) {
    existingFontLink.remove();
  }

  // Apply font family directly
  document.body.style.fontFamily = preset.fontFamily;
  root.style.fontFamily = preset.fontFamily;

  // ── Glassmorphism toggle ──
  if (cardStyle.backdropBlur !== "none") {
    root.classList.add("theme-glassmorphism");
  } else {
    root.classList.remove("theme-glassmorphism");
  }
}

/**
 * Get the MongoDB collection prefix based on company name
 * Converts "SalesGrid" to "salesgrid_", "My Company" to "my_company_"
 */
export function getCollectionPrefix(): string {
  return branding.company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") + "_";
}

/**
 * Get a prefixed collection name
 * Example: getCollectionName("users") returns "salesgrid_users"
 */
export function getCollectionName(baseName: string): string {
  return getCollectionPrefix() + baseName;
}

/**
 * Get a prefixed model name (PascalCase)
 * Example: getModelName("User") returns "SalesGrid_User"
 */
export function getModelName(baseName: string): string {
  const prefix = branding.company.name.replace(/[^a-zA-Z0-9]+/g, "");
  return `${prefix}_${baseName}`;
}
